import type { App as AppType, Directive, InjectionKey } from 'vue'
import type { RouteLocation } from 'vue-router'
import { gtagReady } from '@/components/layout/ManageCookies/ManageCookiesCompose'

let App: AppType | null = null

/**
 * visible in GlobalProperties
 */
const utilsGlobalProperties = {
  getAppRefer: () => {
    return App
  },
  getApp: () => {
    return App?.config?.globalProperties || null
  }
}

/**
 * visible in utilsPlugin (imported globally by AutoImport package)
 */
function nextLoopEvent(delay = 0) {
  return new Promise((resolve) => {
    setTimeout(resolve, delay)
  })
}

function injectStrict<T>(key: InjectionKey<T>, fallback?: T) {
  const resolved = inject(key, fallback)
  if (!resolved) {
    throw new Error(`Could not resolve ${key.description}`)
  }
  return resolved
}

function getFullRouteData(routePath: string, silent = false): undefined | RouteLocation {
  try {
    const globalProperties = utilsGlobalProperties.getApp()
    if (!globalProperties) {
      return undefined
    }
    return globalProperties.$router.resolve({ path: routePath })
  } catch (e) {
    if (!silent) {
      console?.error?.(e, routePath)
    }
  }
}

function isNotNullGuard<T>(data: T | null): data is T {
  return (data as T) !== null
}

function siteGtag() {
  if (!gtagReady.value) {
    return null
  }
  return App?.config?.globalProperties?.$gtag || null
}

const focusDirective: Directive = {
  mounted(el) {
    const input = el?.querySelector('input')
    if (input) {
      setTimeout(() => {
        input?.focus()
      }, 300)
    }
  }
}

const utilsPlugin = {
  ...utilsGlobalProperties,
  nextLoopEvent,
  injectStrict,
  getFullRouteData,
  isNotNullGuard,
  siteGtag
}

/**
 * require import
 */

/**
 * register  utilsGlobalProperties as $utils
 */
export default {
  install: (Vue: AppType) => {
    Vue.config.globalProperties.$utils = utilsGlobalProperties
    App = Vue
    Vue.directive('focus', focusDirective)
  }
}
/**
 * export utilsPlugin (imported globally by AutoImport package)
 * rest of exports require manual import
 */
export { utilsPlugin }
